import { TooltipLayer, TooltipRow, useTooltip } from './Tooltip';
import { useMeasure } from './useMeasure';

export interface BarDatum {
  name: string;
  value: number;
  /** Sekundärt värde som visas i tooltipen, t.ex. föregående period. */
  secondary?: { label: string; value: number };
  /**
   * Ett större tal som stapeln också ska visa, ljusare och bakom det egna.
   * Används när klicket ger fler träffar än stapeln mäter.
   */
  extra?: { label: string; value: number };
}

export interface BarListProps {
  data: BarDatum[];
  color?: string;
  valueSuffix?: string;
  onSelect?: (name: string) => void;
  selected?: string[];
  emptyText?: string;
}

const ROW_HEIGHT = 30;
const BAR_HEIGHT = 12;
const LABEL_WIDTH = 168;
const VALUE_WIDTH = 44;
const WIDE_VALUE_WIDTH = 86;
const CORNER = 4;

/**
 * En serie, en färg. Staplarna får aldrig färgas mörkare ju större de är – det
 * vore att dubbelkoda längden och bränna den enda lediga kanalen.
 */
export function BarList({ data, color = 'var(--series-1)', valueSuffix = '', onSelect, selected, emptyText }: BarListProps) {
  const { tooltip, show, hide } = useTooltip();
  const [containerRef, containerWidth] = useMeasure<HTMLDivElement>(560);

  if (data.length === 0) {
    return <p className="empty">{emptyText ?? 'Inga träffar i urvalet.'}</p>;
  }

  // Båda talen mäts mot samma axel, annars jämför man olika saker i samma bild.
  const hasExtra = data.some((d) => d.extra && d.extra.value > d.value);
  const valueWidth = hasExtra ? WIDE_VALUE_WIDTH : VALUE_WIDTH;
  const max = Math.max(...data.map((d) => Math.max(d.value, d.extra?.value ?? 0)), 1);
  const height = data.length * ROW_HEIGHT;
  const width = Math.max(320, containerWidth);

  return (
    <div ref={containerRef}>
      <svg
        className="chart"
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMinYMin meet"
        role="img"
        aria-label="Stapeldiagram"
      >
        {data.map((datum, index) => {
          const y = index * ROW_HEIGHT;
          const trackWidth = width - LABEL_WIDTH - valueWidth;
          const barWidth = Math.max(2, (datum.value / max) * trackWidth);
          const extraWidth = datum.extra ? Math.max(barWidth, (datum.extra.value / max) * trackWidth) : 0;
          const isSelected = selected?.includes(datum.name);

          return (
            <g
              key={datum.name}
              onMouseMove={(event) =>
                show(
                  event,
                  <>
                    <div className="tooltip__title">{datum.name}</div>
                    <TooltipRow color={color} label="Uppdrag" value={datum.value} />
                    {datum.extra ? <TooltipRow label={datum.extra.label} value={datum.extra.value} /> : null}
                    {datum.secondary ? (
                      <TooltipRow label={datum.secondary.label} value={datum.secondary.value} />
                    ) : null}
                  </>,
                )
              }
              onMouseLeave={hide}
              onClick={() => onSelect?.(datum.name)}
              style={{ cursor: onSelect ? 'pointer' : 'default' }}
            >
              <rect x={0} y={y} width={width} height={ROW_HEIGHT} fill="transparent" />
              <text
                className="chart__label"
                x={0}
                y={y + ROW_HEIGHT / 2 + 4}
                fill={isSelected ? 'var(--text-primary)' : undefined}
                fontWeight={isSelected ? 650 : 400}
              >
                {truncate(datum.name, 24)}
              </text>
              {datum.extra && extraWidth > barWidth ? (
                <rect
                  x={LABEL_WIDTH}
                  y={y + (ROW_HEIGHT - BAR_HEIGHT) / 2}
                  width={extraWidth}
                  height={BAR_HEIGHT}
                  rx={CORNER}
                  fill={color}
                  opacity={0.35}
                />
              ) : null}
              <rect
                x={LABEL_WIDTH}
                y={y + (ROW_HEIGHT - BAR_HEIGHT) / 2}
                width={barWidth}
                height={BAR_HEIGHT}
                rx={CORNER}
                fill={color}
                opacity={selected && selected.length > 0 && !isSelected ? 0.5 : 1}
              />
              <text className="chart__value" x={width} y={y + ROW_HEIGHT / 2 + 4} textAnchor="end">
                {datum.value}
                {valueSuffix}
                {datum.extra && datum.extra.value > datum.value ? (
                  <tspan className="chart__value--muted"> · {datum.extra.value}</tspan>
                ) : null}
              </text>
              <title>{`${datum.name}: ${datum.value}${valueSuffix}`}</title>
            </g>
          );
        })}
      </svg>
      <TooltipLayer tooltip={tooltip} />
    </div>
  );
}

function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max - 1)}…`;
}
